import React, { useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:3051";

interface Schedule {
  id: string;
  name: string;
  start_at: string;
  end_at: string;
  days: number[];
  enabled: boolean;
}

interface ScheduleManagerProps {
  schedules: Schedule[];
  password: string;
  onSchedulesUpdate: () => void;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ScheduleManager: React.FC<ScheduleManagerProps> = ({
  schedules,
  password,
  onSchedulesUpdate,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    start_at: "22:00",
    end_at: "06:00",
    days: [] as number[],
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const resetForm = () => {
    setFormData({
      name: "",
      start_at: "22:00",
      end_at: "06:00",
      days: [],
    });
    setShowAddForm(false);
    setEditingSchedule(null);
  };

  const handleDayToggle = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day].sort(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const url = editingSchedule
        ? `${API_BASE_URL}/api/dominant-monitoring/config/schedule/${editingSchedule.id}`
        : `${API_BASE_URL}/api/dominant-monitoring/config/schedule`;

      const method = editingSchedule ? "put" : "post";

      const response = await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-admin-password": password,
        },
      });

      if (response.data.success) {
        setMessage({
          type: "success",
          text: editingSchedule
            ? "Schedule updated successfully"
            : "Schedule added successfully",
        });
        resetForm();
        onSchedulesUpdate();
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to save schedule",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setFormData({
      name: schedule.name,
      start_at: schedule.start_at,
      end_at: schedule.end_at,
      days: schedule.days,
    });
    setEditingSchedule(schedule);
    setShowAddForm(true);
  };

  const handleDelete = async (scheduleId: string) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API_BASE_URL}/api/dominant-monitoring/config/schedule/${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        setMessage({
          type: "success",
          text: "Schedule deleted successfully",
        });
        onSchedulesUpdate();
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to delete schedule",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (schedule: Schedule) => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/api/dominant-monitoring/config/schedule/${schedule.id}`,
        { enabled: !schedule.enabled },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-admin-password": password,
          },
        }
      );

      if (response.data.success) {
        onSchedulesUpdate();
      }
    } catch (err: any) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to toggle schedule",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">
          📅 Abandoned Period Schedules
        </h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          {showAddForm ? "Cancel" : "+ Add Schedule"}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-md ${
            message.type === "success"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <p
            className={`text-sm ${
              message.type === "success" ? "text-green-800" : "text-red-800"
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 p-4 bg-gray-50 rounded-lg"
        >
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingSchedule ? "Edit Schedule" : "Add New Schedule"}
          </h3>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Weeknight Closure"
                required
              />
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_at}
                  onChange={(e) =>
                    setFormData({ ...formData, start_at: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_at}
                  onChange={(e) =>
                    setFormData({ ...formData, end_at: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Days Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDayToggle(day)}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      formData.days.includes(day)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {DAY_NAMES[day - 1]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select the days when this schedule should be active
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || formData.days.length === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving
                  ? "Saving..."
                  : editingSchedule
                  ? "Update Schedule"
                  : "Add Schedule"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Schedules List */}
      <div className="space-y-3">
        {schedules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No schedules configured yet.</p>
            <p className="text-sm mt-1">
              Click "Add Schedule" to create your first abandoned period
              schedule.
            </p>
          </div>
        ) : (
          schedules.map((schedule) => (
            <div
              key={schedule.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      {schedule.name}
                    </h3>
                    <button
                      onClick={() => handleToggleEnabled(schedule)}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        schedule.enabled ? "bg-green-600" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          schedule.enabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="text-sm text-gray-600 space-y-1">
                    <p>
                      <span className="font-medium">Time:</span>{" "}
                      {schedule.start_at} → {schedule.end_at}
                      {schedule.start_at > schedule.end_at && (
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                          Overnight
                        </span>
                      )}
                    </p>
                    <p>
                      <span className="font-medium">Days:</span>{" "}
                      {schedule.days.map((d) => DAY_NAMES[d - 1]).join(", ")}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(schedule)}
                    disabled={saving}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(schedule.id)}
                    disabled={saving}
                    className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ScheduleManager;
